/**
 * EmailVerificationBanner — Sprint 30.
 *
 * Shown when /auth/me reports email_verified=false. Soft gate: purely
 * informational plus a resend action; the backend hard-blocks only
 * invite-sending. Dismissal lasts for the session (state lives in
 * MainLayout's single mount, like TrialBanner).
 */
import { useState } from 'react'
import { MailWarning, X } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from '../../i18n/I18nContext'
import { emailVerificationService } from '../../services/emailVerification'

export function EmailVerificationBanner() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(false)
  const [sending, setSending] = useState(false)

  if (!user || user.email_verified || dismissed) return null

  const resend = async () => {
    setSending(true)
    try {
      await emailVerificationService.resendVerification()
      toast.success(t('auth.verify.resent'))
    } catch {
      toast.error(t('auth.verify.resendError'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="w-full bg-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 font-medium">
        <MailWarning size={14} />
        <span>{t('auth.verify.bannerText')}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={resend}
          disabled={sending}
          className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? t('auth.verify.resending') : t('auth.verify.resend')}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 hover:bg-white/20 rounded transition-colors"
          aria-label={t('auth.verify.dismiss')}
        >
          <X size={13} />
        </button>
      </div>
    </div>
  )
}
